"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import {
  getActivePsRuleHumanReviewRun,
  listProblemStatementRuns,
  type ProblemStatementRun,
  type PsRuleHumanReviewRun,
} from "@/services/api/case-checking";
import { normalizePsRuleHumanReviewRun } from "@/components/case-checking/humanRuleReviewCascade";

export type CaseDetailDataContextValue = {
  caseId: string;
  problemStatementRun: ProblemStatementRun | null;
  psRunLoading: boolean;
  psRunError: string | null;
  setProblemStatementRun: (run: ProblemStatementRun | null) => void;
  reloadProblemStatementRun: () => Promise<ProblemStatementRun | null>;
  psRuleReviewRun: PsRuleHumanReviewRun | null;
  psRuleReviewLoading: boolean;
  psRuleReviewError: string | null;
  /** PS run id once a read-only active-review fetch has finished (including null). */
  psRuleReviewFetchSettledForPsRunId: string | null;
  setPsRuleReviewRun: (run: PsRuleHumanReviewRun | null) => void;
  ensurePsRuleReviewRun: (createIfMissing?: boolean) => Promise<PsRuleHumanReviewRun | null>;
  /** Refetch the latest PS run; the active review run is then reloaded for it. */
  reloadActiveReview: () => Promise<void>;
};

const CaseDetailDataContext = createContext<CaseDetailDataContextValue | null>(null);

export function CaseDetailDataProvider({
  caseId,
  enabled,
  children,
}: {
  caseId: string;
  enabled: boolean;
  children: ReactNode;
}) {
  const [problemStatementRun, setProblemStatementRun] = useState<ProblemStatementRun | null>(null);
  const [psRunLoading, setPsRunLoading] = useState(enabled);
  const [psRunError, setPsRunError] = useState<string | null>(null);
  const [psRuleReviewRun, setPsRuleReviewRunState] = useState<PsRuleHumanReviewRun | null>(null);
  const [psRuleReviewLoading, setPsRuleReviewLoading] = useState(false);
  const [psRuleReviewError, setPsRuleReviewError] = useState<string | null>(null);
  /** PS run id for which a read-only active-review fetch has finished (run or null). Stops retry loops. */
  const [psRuleReviewFetchSettledForPsRunId, setPsRuleReviewFetchSettledForPsRunId] = useState<string | null>(null);

  const psReviewInflightKeyRef = useRef<string | null>(null);
  const psReviewInflightRef = useRef<Promise<PsRuleHumanReviewRun | null> | null>(null);
  /** Bumped when the active review run is replaced (restart, patch) so stale GETs cannot overwrite. */
  const psRuleReviewEpochRef = useRef(0);

  const setPsRuleReviewRun = useCallback((run: PsRuleHumanReviewRun | null) => {
    psRuleReviewEpochRef.current += 1;
    psReviewInflightKeyRef.current = null;
    psReviewInflightRef.current = null;
    setPsRuleReviewRunState(run ? normalizePsRuleHumanReviewRun(run) : null);
    if (!run) setPsRuleReviewFetchSettledForPsRunId(null);
  }, []);

  const reloadProblemStatementRun = useCallback(async () => {
    if (!enabled) return null;
    setPsRunLoading(true);
    setPsRunError(null);
    try {
      const runs = await listProblemStatementRuns(caseId, 1);
      const run = runs[0] ?? null;
      setProblemStatementRun(run);
      return run;
    } catch (e) {
      setPsRunError(e instanceof Error ? e.message : "Failed to load outcomes");
      setProblemStatementRun(null);
      return null;
    } finally {
      setPsRunLoading(false);
    }
  }, [caseId, enabled]);

  useEffect(() => {
    if (!enabled) {
      setProblemStatementRun(null);
      setPsRunLoading(false);
      setPsRunError(null);
      return;
    }
    let cancelled = false;
    void reloadProblemStatementRun().finally(() => {
      if (!cancelled && !enabled) setPsRunLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [caseId, enabled, reloadProblemStatementRun]);

  useEffect(() => {
    psRuleReviewEpochRef.current += 1;
    setPsRuleReviewRunState(null);
    setPsRuleReviewError(null);
    setPsRuleReviewFetchSettledForPsRunId(null);
    psReviewInflightKeyRef.current = null;
    psReviewInflightRef.current = null;
  }, [problemStatementRun?.id]);

  const ensurePsRuleReviewRun = useCallback(
    async (createIfMissing = false): Promise<PsRuleHumanReviewRun | null> => {
      const psRunId = problemStatementRun?.id;
      if (!psRunId) return null;

      const inflightKey = `${caseId}:${psRunId}:${createIfMissing ? "create" : "read"}`;
      if (psReviewInflightRef.current && psReviewInflightKeyRef.current === inflightKey) {
        return psReviewInflightRef.current;
      }

      if (createIfMissing) setPsRuleReviewFetchSettledForPsRunId(null);

      setPsRuleReviewLoading(true);
      setPsRuleReviewError(null);
      psReviewInflightKeyRef.current = inflightKey;
      const epochAtStart = psRuleReviewEpochRef.current;

      const promise = getActivePsRuleHumanReviewRun(caseId, psRunId, createIfMissing)
        .then((active) => {
          if (epochAtStart === psRuleReviewEpochRef.current) {
            setPsRuleReviewRunState(active ? normalizePsRuleHumanReviewRun(active) : null);
            setPsRuleReviewFetchSettledForPsRunId(psRunId);
          }
          return active;
        })
        .catch((e) => {
          const message = e instanceof Error ? e.message : "Failed to load rule reviews";
          setPsRuleReviewError(message);
          if (epochAtStart === psRuleReviewEpochRef.current) {
            setPsRuleReviewFetchSettledForPsRunId(psRunId);
          }
          return null;
        })
        .finally(() => {
          setPsRuleReviewLoading(false);
          if (psReviewInflightKeyRef.current === inflightKey) {
            psReviewInflightKeyRef.current = null;
            psReviewInflightRef.current = null;
          }
        });

      psReviewInflightRef.current = promise;
      return promise;
    },
    [caseId, problemStatementRun?.id],
  );

  const reloadActiveReview = useCallback(async () => {
    const previousPsRunId = problemStatementRun?.id;
    const run = await reloadProblemStatementRun();
    if (run && run.id === previousPsRunId) setPsRuleReviewRun(null);
  }, [problemStatementRun?.id, reloadProblemStatementRun, setPsRuleReviewRun]);

  useEffect(() => {
    if (!enabled || psRunLoading || !problemStatementRun?.id) return;
    if (psRuleReviewRun || psRuleReviewLoading) return;
    if (psRuleReviewFetchSettledForPsRunId === problemStatementRun.id) return;
    void ensurePsRuleReviewRun(false);
  }, [
    enabled,
    ensurePsRuleReviewRun,
    problemStatementRun?.id,
    psRunLoading,
    psRuleReviewLoading,
    psRuleReviewRun,
    psRuleReviewFetchSettledForPsRunId,
  ]);

  const value = useMemo<CaseDetailDataContextValue>(
    () => ({
      caseId,
      problemStatementRun,
      psRunLoading,
      psRunError,
      setProblemStatementRun,
      reloadProblemStatementRun,
      psRuleReviewRun,
      psRuleReviewLoading,
      psRuleReviewError,
      psRuleReviewFetchSettledForPsRunId,
      setPsRuleReviewRun,
      ensurePsRuleReviewRun,
      reloadActiveReview,
    }),
    [
      caseId,
      problemStatementRun,
      psRunLoading,
      psRunError,
      reloadProblemStatementRun,
      psRuleReviewRun,
      psRuleReviewLoading,
      psRuleReviewError,
      psRuleReviewFetchSettledForPsRunId,
      setPsRuleReviewRun,
      ensurePsRuleReviewRun,
      reloadActiveReview,
    ],
  );

  return <CaseDetailDataContext.Provider value={value}>{children}</CaseDetailDataContext.Provider>;
}

export function useCaseDetailData(): CaseDetailDataContextValue | null {
  return useContext(CaseDetailDataContext);
}
